import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'

import {
  Seo,
  Anteroom,
  PlayerLanding,
  Flixx,
  ErrorCard,
  Loader
} from 'ui'
import { useIsPurchased, useSession } from 'hooks'
import {
  useBuyContent,
  useGetByCategory,
  useGetContent,
  useGetProfileMinimal
} from 'api/content'
import { useGetBalance } from 'api/user'
import {
  useGetAd,
  useGetNextReward,
  usePostWatchedAd
} from 'api/ads'

import { getThumbnail } from 'utils/thumbnails'
import { formatAmount } from 'utils/amounts'
import { formatDuration } from 'utils/duration'
import { GAHandler } from 'utils/GA'

import { ContentInfo } from './ContentInfo'
import ContentCard from './ContentCard'
import useModal from 'hooks/useModal'


const AnteroomSerie = () => {

  const { contentUUID } = useParams()

  const { t } = useTranslation()
  const { Modal } = useModal('welcome')

  const { isLoggedIn } = useSession()
  const { data: balance } = useGetBalance()
  const { data: content, error: contentError } = useGetContent({ uuid: contentUUID, contentType: 'content' })
  const { data: author } = useGetProfileMinimal({ userID: content?.author.id })

  const { data: similar, isLoading } = useGetByCategory({
    categoryId: content?.category.id,
    contentType: 'community',
    filter: (c) => c.uuid !== content?.uuid
  })

  const { isPurchased, isPurchasesLoaded } = useIsPurchased({ contentType: 'content' })

  const {
    data: ad,
    refetch: getAd,
    remove: resetAd
  } = useGetAd()

  const {
    data: nextReward,
    refetch: getNextReward,
    remove: resetNextReward
  } = useGetNextReward()

  const { buyContent } = useBuyContent()
  const { postWatchedAd } = usePostWatchedAd()

  const [showAd, setShowAd] = useState(false)

  const [currentlyWatching, setCurrentlyWatching] = useState()

  const headerInfo = {
    serie: content?.title,
    seasonNumber: 999999999999,
    episodeNumber: 9999999999999,
    episodeTitle: currentlyWatching?.title
  }

  const handlePlayContent = () => {
    setCurrentlyWatching(content)
  }

  const handleBuyContent = () => {
    buyContent({
      contentId: content.uuid,
      price: content.price
    })
      .then(handlePlayContent)
  }

  const handleCloseAd = () => {
    resetAd()
    resetNextReward()
    setShowAd(false)
  }

  const handleWatchedAd = () => {
    postWatchedAd({ id: ad.id })
      .then(handleBuyContent)

    handleCloseAd()
  }

  const handleClick = () => {
    if (!isLoggedIn) {
      Modal.open()
      GAHandler('ui_watch_notloggedin');
      return
    }

    if (isPurchased(content.uuid)) {
      handlePlayContent()
      GAHandler('ui_watch_content');
      return
    }

    const canPurchaseIt = parseFloat(content.price) <= balance

    if (canPurchaseIt) {
      handleBuyContent()
      GAHandler('ui_watch_buy');
    } else {
      getNextReward()
      getAd()
      GAHandler('ui_watch_ad');
    }
  }


  useEffect(() => {
    if (ad && nextReward) setShowAd(true)
  }, [ad, nextReward])


  if (contentError?.code === 404) {
    return (
      <div style={{ height: '80vh', display: 'grid' }}>
        <ErrorCard
          description={t('contentNotFound')}
          code='W3001'
        />
      </div>
    )
  }


  if (!content) {
    return <Loader />
  }


  if (currentlyWatching) {
    return (
      <PlayerLanding
        video={{ ...currentlyWatching, headerInfo }}
        onClose={() => setCurrentlyWatching(null)}
        // nextEpisode={nextEpisode}
        // onNextEpisode={handleNextEpisode}
        title={content.title}
      />
    )
  }

  const authorData = {
    name: author?.displayName || content.author.displayName,
    avatar: author?.avatar || content.author.Profile.avatar,
    videos: author?.numContents
  }

  const cover = {
    src: getThumbnail(content),
    alt: content.title
  }

  const textCTA = !isLoggedIn || isPurchased(content?.uuid)
    ? t('watchNow')
    : () => (
      <>
        <span>
          {t('watchFor')}
        </span>
        <Flixx
          price={content.price.substring(0, 4)}
          variant='text'
          size='xl'
        />
      </>
    )


  return (
    <Anteroom.Root>

      <Seo
        title={content.title}
        description={content.body}
      />

      <Anteroom.Info
        onClickCTA={handleClick}
        textCTA={textCTA}
        author={authorData}
        cover={cover}
        urlShare={`https://play.flixxo.com/community/${content.uuid}`}
      >
        <ContentInfo
          title={content.title}
          duration={formatDuration(content.duration)}
          body={content.body}
          audios={content.audioLang}
          subtitles={content.subtitle}
        />
      </Anteroom.Info>

      <Anteroom.Carousel.Title>
        SIMILAR
      </Anteroom.Carousel.Title>

      <Anteroom.Carousel.Slides key={content.uuid}>
        {similar?.map(cont => (
          <ContentCard
            key={cont.uuid}
            to={`/community/${cont.uuid}`}
            title={cont.title}
            duration={`${cont.duration} ${t('min')}`}
            body={cont.body}
            cover={getThumbnail(cont)}
            isPurchased={isPurchased(cont.uuid)}
            price={formatAmount(cont.price)}
          />
        ))}
      </Anteroom.Carousel.Slides>
    </Anteroom.Root>
  )
}

export default AnteroomSerie